import React, { useEffect, useRef, useState } from "react";
import { opt } from "@/lib/nativeSelect";
import { AlertTriangle, ImagePlus, X, Loader2, GripVertical, ChevronLeft, ChevronRight, ScanLine, Smartphone, CheckCircle2, Car, Bike, Truck, Caravan, Ship, Tractor, Zap } from "lucide-react";
import QRCode from "qrcode";
import { api, mediaUrl } from "@/lib/api";
import { crm, crmMediaUpload } from "@/lib/crm";
import { compressImage } from "@/lib/image";
import { FUEL_OPTIONS } from "@/data/fuels";
import { isEV, isPureElectric } from "@/data/ev";
import { OPTION_GROUPS } from "@/data/optionGroups";
import { useMakes, modelsFor } from "@/lib/useMakes";
import { detectVehicleType } from "@/lib/classify";
import { variantsFor } from "@/data/modelVariants";
import MakeCombobox from "@/components/MakeCombobox";
import { formatKm } from "@/lib/format";
import { useT } from "@/i18n";
import DamageInput from "@/components/DamageInput";
import { normalizeZones } from "@/components/CarDamageSchema";
import ColorSelect from "@/components/ColorSelect";
import WatermarkPreview from "@/components/WatermarkPreview";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import PriceAdvice from "@/components/PriceAdvice";
import { toast } from "sonner";
import PhotoThumb from "@/components/PhotoThumb";

const MAX_LISTING_PHOTOS = 12;
const EURO_NORMS = ["Euro 1", "Euro 2", "Euro 3", "Euro 4", "Euro 5", "Euro 6", "Euro 6d", "Euro 7"];
const VEHICLE_TYPES = [
  { key: "car", label: "Voiture", icon: Car },
  { key: "moto", label: "Moto", icon: Bike },
  { key: "van", label: "Utilitaire", icon: Truck },
  { key: "camper", label: "Camping-car", icon: Caravan },
  { key: "truck", label: "Camion", icon: Truck },
  { key: "agri", label: "Agricole", icon: Tractor },
  { key: "boat", label: "Bateau", icon: Ship },
];
const BIKE_TYPES = ["Roadster", "Sportive", "Trail / Adventure", "Custom", "Routière / GT", "Cross / Enduro", "Supermotard", "Scooter", "Café Racer", "Cruiser"];

function dataUrlToBlob(dataUrl) {
  const [head, b64] = String(dataUrl).split(",");
  const mime = (head.match(/data:(.*?);/) || [])[1] || "image/jpeg";
  const bin = atob(b64 || "");
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

function L({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-1 block text-xs font-semibold text-white/70">{label}</span>
      {children}
    </label>
  );
}

// Shared listing editor (used in the PRO fleet AND directly on the car detail
// page for the owner). Reorder photos by drag & drop — the first photo is the
// main one shown on cards and search results.
export default function ListingEditModal({ car, onClose, onSaved }) {
  const { t, lang } = useT();
  const makes = useMakes();
  const [vt, setVt] = useState(() => detectVehicleType(car));
  const [bikeType, setBikeType] = useState(car.bike_type || "");
  const [f, setF] = useState({
    make: car.make || "", model: car.model || "", variant: car.variant || "",
    price: car.price, mileage: car.mileage, power: car.power || "", color: car.color || "",
    location: car.location || "", year: car.year || "", fuel: car.fuel || "",
    transmission: car.transmission || "", body_type: car.body_type || "",
    first_registration: car.first_registration || "", doors: car.doors || "", seats: car.seats || "",
    condition: car.condition || "Occasion", vat_deductible: car.vat_deductible ?? false,
    co2: car.co2 ?? "", displacement: car.displacement ?? "", euro_norm: car.euro_norm || "",
    phone: "",
    negotiable: car.negotiable ?? false, description: car.description || "",
  });
  const [options, setOptions] = useState(() => Array.isArray(car.options) ? car.options.filter(Boolean) : []);
  const [ev, setEv] = useState(() => ({
    battery_kwh: car.ev?.battery_kwh ?? "", soh: car.ev?.soh ?? "",
    charge_kw: car.ev?.charge_kw ?? "", range_wltp: car.ev?.range_wltp ?? car.range_wltp ?? "",
  }));
  const updEv = (k, v) => setEv((p) => ({ ...p, [k]: v }));
  const toggleOption = (o) => setOptions((prev) => prev.includes(o) ? prev.filter((x) => x !== o) : [...prev, o]);
  const models = modelsFor(makes, f.make, vt);
  const variantOpts = vt === "car" ? variantsFor(f.make, f.model) : [];
  const [accidented, setAccidented] = useState(false);
  const [damageZones, setDamageZones] = useState([]);
  const [honestyNote, setHonestyNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [photos, setPhotos] = useState(() => (car.images || []).filter(Boolean));
  const photosTouched = useRef(false);
  const removedSet = useRef(new Set());
  const [removed, setRemoved] = useState([]);
  const [watermark, setWatermark] = useState(true);
  const [hasCleanOriginals, setHasCleanOriginals] = useState(false);
  const cleanRef = useRef(new Set());
  const [wmText, setWmText] = useState(car.seller_name || car.dealer || "Pactly Car");
  const [photoBusy, setPhotoBusy] = useState(0);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const photoInputRef = useRef(null);
  const [photoQrOpen, setPhotoQrOpen] = useState(false);
  const [photoQr, setPhotoQr] = useState(null);
  const [photoRecv, setPhotoRecv] = useState(0);
  const photoPollRef = useRef(null);
  const photoSeenRef = useRef(new Set());
  const cgRef = useRef(null);
  const [cgBusy, setCgBusy] = useState(false);
  const upd = (k, v) => setF((p) => ({ ...p, [k]: v }));

  // Respect an existing saved category override; otherwise keep the auto-detected type.
  useEffect(() => {
    let alive = true;
    crm(`/car-meta/batch?ids=${encodeURIComponent(car.id)}`).then((d) => {
      if (!alive) return;
      const ov = d?.meta?.[String(car.id)]?.override || {};
      if (ov.vehicle_type) setVt(String(ov.vehicle_type).toLowerCase());
      if (ov.bike_type) setBikeType(ov.bike_type);
    }).catch(() => {});
    return () => { alive = false; };
  }, [car.id]);

  const importCarteGrise = async (file) => {
    if (!file) return;
    setCgBusy(true);
    try {
      const sess = await crm("/scan/session", { method: "POST", timeout: 10000 });
      const sid = sess.session_id;
      const fd = new FormData();
      fd.append("file", file, file.name || "carte-grise.jpg");
      const token = localStorage.getItem("pactly_session_token") || sessionStorage.getItem("pactly_session_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scan/${sid}/upload`, {
        method: "POST", headers: token ? { Authorization: `Bearer ${token}` } : {}, body: fd,
      });
      if (!res.ok) throw new Error("Extraction IA échouée. Réessayez avec une photo nette.");
      const { fields } = await res.json();
      const nf = {};
      if (fields?.co2 != null && fields.co2 !== "") nf.co2 = String(fields.co2);
      if (fields?.displacement != null && fields.displacement !== "") nf.displacement = String(fields.displacement);
      if (fields?.euro_norm && EURO_NORMS.includes(fields.euro_norm)) nf.euro_norm = fields.euro_norm;
      if (fields?.power_kw && !String(f.power || "").trim()) {
        const bhp = Math.round(Number(fields.power_kw) * 1.341);
        if (bhp > 0) nf.power = String(bhp);
      }
      const seats = parseInt(fields?.seats, 10);
      if (seats >= 1 && seats <= 9 && !String(f.seats || "").trim()) nf.seats = String(seats);
      const doors = parseInt(fields?.doors, 10);
      if (doors >= 2 && doors <= 5 && !String(f.doors || "").trim()) nf.doors = String(doors);
      if (Object.keys(nf).length) {
        setF((p) => ({ ...p, ...nf }));
        const label = fields?.doc_type === "coc" ? "Certificat de conformité" : "Carte grise";
        toast.success(`${label} analysé${fields?.country ? " (" + String(fields.country).toUpperCase() + ")" : ""} — champs remplis.`);
      } else {
        toast.message("Aucune donnée exploitable détectée sur le document.");
      }
    } catch (e) {
      toast.error(e.message || "Extraction échouée. Réessayez avec une photo plus nette.");
    } finally {
      setCgBusy(false);
    }
  };

  const removePhoto = (i) => {
    photosTouched.current = true;
    setPhotos((prev) => {
      const url = prev[i];
      if (url) removedSet.current.add(url);
      if (url && /\/api\/media\/file\//.test(url)) setRemoved((r) => [...r, url]);
      return prev.filter((_, idx) => idx !== i);
    });
  };
  const reorder = (from, to) => {
    if (from == null || to == null || from === to) return;
    setPhotos((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };
  const onAddPhotos = async (e) => {
    photosTouched.current = true;
    const files = Array.from(e.target.files || []).slice(0, Math.max(0, MAX_LISTING_PHOTOS - photos.length));
    e.target.value = "";
    for (const file of files) {
      let durl;
      try { durl = await compressImage(file); } catch { toast.error("Photo illisible, ignorée."); continue; }
      setPhotoBusy((n) => n + 1);
      try {
        const { url } = await crmMediaUpload(dataUrlToBlob(durl), "photo.jpg");
        cleanRef.current.add(url);
        setPhotos((prev) => (prev.length < MAX_LISTING_PHOTOS ? [...prev, url] : prev));
      } catch (err) { toast.error(err?.message || "Échec de l'envoi."); }
      finally { setPhotoBusy((n) => Math.max(0, n - 1)); }
    }
  };

  // ---- Cross-device photo import (QR): phone → this editor in near real time --
  const stopPhotoPoll = () => { if (photoPollRef.current) { clearInterval(photoPollRef.current); photoPollRef.current = null; } };
  const pollPhotos = (sid) => {
    stopPhotoPoll();
    photoPollRef.current = setInterval(async () => {
      try {
        const s = await crm(`/photo-session/${sid}?t=${Date.now()}`, { method: "GET", timeout: 8000 });
        let added = 0;
        for (const url of (s.photos || [])) {
          if (photoSeenRef.current.has(url)) continue;
          photoSeenRef.current.add(url);
          cleanRef.current.add(url);
          photosTouched.current = true;
          setPhotos((prev) => (prev.length < MAX_LISTING_PHOTOS ? [...prev, url] : prev));
          added++;
        }
        if (added) { setPhotoRecv(photoSeenRef.current.size); toast.success(`${added} photo${added > 1 ? "s" : ""} reçue${added > 1 ? "s" : ""} du téléphone`); }
        else if (typeof s.count === "number") setPhotoRecv((c) => Math.max(c, (s.photos || []).length));
        if (s.expired) stopPhotoPoll();
      } catch { /* keep polling */ }
    }, 1500);
  };
  const startPhoneImport = async () => {
    if (photos.length >= MAX_LISTING_PHOTOS) { toast.error(`Maximum ${MAX_LISTING_PHOTOS} photos.`); return; }
    let sid;
    try { const r = await crm("/pro/photo-session", { method: "POST", timeout: 10000 }); sid = r.session_id; }
    catch { toast.error("Impossible de démarrer. Réessayez."); return; }
    photoSeenRef.current = new Set(); setPhotoRecv(0);
    try {
      const url = `${window.location.origin}/upload-photos/${sid}`;
      setPhotoQr(await QRCode.toDataURL(url, { width: 320, margin: 1, color: { dark: "#0a1024", light: "#ffffff" } }));
    } catch { setPhotoQr(null); }
    setPhotoQrOpen(true);
    pollPhotos(sid);
  };
  const closePhoneImport = () => { stopPhotoPoll(); setPhotoQrOpen(false); };
  useEffect(() => () => stopPhotoPoll(), []);

  useEffect(() => {
    crm(`/car-meta/${car.id}`).then((m) => {
      setAccidented(!!m.accidented); setDamageZones(normalizeZones(m.damage_zones)); setHonestyNote(m.honesty_note || "");
      if (m.vat_deductible != null) setF((p) => ({ ...p, vat_deductible: !!m.vat_deductible }));
      if (m.override?.seller_phone) setF((p) => ({ ...p, phone: m.override.seller_phone }));
      if (m.display_seller?.name) setWmText(m.display_seller.name);
      if (m.override) setF((p) => ({ ...p,
        co2: m.override.co2 != null ? m.override.co2 : p.co2,
        displacement: m.override.engine_cc != null ? m.override.engine_cc : p.displacement,
        euro_norm: m.override.euro_norm || p.euro_norm,
      }));
      if (m.override?.ev) setEv((p) => ({
        battery_kwh: m.override.ev.battery_kwh ?? p.battery_kwh,
        soh: m.override.ev.soh ?? p.soh,
        charge_kw: m.override.ev.charge_kw ?? p.charge_kw,
        range_wltp: m.override.ev.range_wltp ?? p.range_wltp,
      }));
      // Non-destructive watermark: prefer the CLEAN originals so the toggle can
      // add or remove the watermark freely. Legacy listings (no originals) fall
      // back to the baked display images — the watermark can only be re-applied.
      const clean = (m.override?.original_images || []).filter(Boolean);
      // Never overwrite photos the seller already added/removed while this loaded.
      const keep = (list) => setPhotos((prev) => {
        if (!photosTouched.current) return list;
        const extra = prev.filter((u) => !(car.images || []).includes(u) && !list.includes(u));
        return [...list.filter((u) => !removedSet.current.has(u)), ...extra].slice(0, MAX_LISTING_PHOTOS);
      });
      if (clean.length) {
        setHasCleanOriginals(true);
        clean.forEach((u) => cleanRef.current.add(u));
        keep(clean);
      } else if (m.override?.images?.length) {
        keep(m.override.images.filter(Boolean));
      }
      if (m.override?.watermark != null) setWatermark(!!m.override.watermark);
    }).catch(() => {});
  }, [car.id]);

  const save = async () => {
    if (photos.length === 0) { toast.error("Ajoutez au moins une photo."); return; }
    setSaving(true);
    let patchErr = null;
    // External listings API (owner-scoped). Isolated so a failure here never
    // blocks the local overrides below (e.g. the seller phone).
    try {
      await api(`/cars/${car.id}`, { method: "PATCH", body: {
        make: f.make || null, model: f.model || null, variant: f.variant || null,
        price: Number(f.price), mileage: Number(f.mileage), power: f.power ? Number(f.power) : null,
        color: f.color, location: f.location, description: f.description,
        year: f.year ? Number(f.year) : null, fuel: f.fuel || null, transmission: f.transmission || null,
        body_type: f.body_type || null, negotiable: !!f.negotiable,
        first_registration: f.first_registration || null,
        doors: f.doors ? Number(f.doors) : null, seats: f.seats ? Number(f.seats) : null,
        condition: f.condition || null,
        engine_cc: f.displacement !== "" ? Number(f.displacement) : null,
        options,
      } });
    } catch (e) { patchErr = e; }

    // Fiscal data (CO2 / cylindrée / norme Euro). The external API only stores
    // engine_cc, so we keep the full set as a display override that merges into
    // the catalogue — this is what re-activates the tax estimate on the cards.
    try {
      await crm("/car-meta/fiscal", { method: "POST", body: {
        car_id: car.id,
        co2: f.co2 !== "" ? Number(f.co2) : null,
        engine_cc: f.displacement !== "" ? Number(f.displacement) : null,
        euro_norm: f.euro_norm || null,
      } });
    } catch {}

    // Pactly Electric — EV data (battery kWh, SOH %, charge kW, WLTP range).
    if (isEV(f.fuel)) {
      try {
        await crm("/car-meta/ev", { method: "POST", body: {
          car_id: car.id,
          battery_kwh: ev.battery_kwh !== "" ? Number(ev.battery_kwh) : null,
          soh: ev.soh !== "" ? Number(ev.soh) : null,
          charge_kw: ev.charge_kw !== "" ? Number(ev.charge_kw) : null,
          range_wltp: ev.range_wltp !== "" ? Number(ev.range_wltp) : null,
        } });
      } catch {}
    }

    // Non-destructive watermark: `photos` holds the CLEAN sources we manage.
    // The public display images are derived — watermarked copies when the toggle
    // is on, clean originals when it's off. Always persisted so the toggle takes
    // effect without deleting/recreating the listing.
    let displayImages = photos;
    if (watermark) {
      const cleanUrls = photos.filter((p) => cleanRef.current.has(p));
      if (cleanUrls.length) {
        try {
          const r = await crm("/media/watermark", { method: "POST", timeout: 60000, body: { images: cleanUrls, text: wmText } });
          const map = {};
          (r.images || []).forEach((u, i) => { map[cleanUrls[i]] = u; });
          displayImages = photos.map((p) => map[p] || p);
        } catch { toast.error("Le filigrane n'a pas pu être appliqué à toutes les photos."); }
      }
    }
    let imagesSaved = false;
    try { await crm("/car-meta/images", { method: "POST", body: { car_id: car.id, images: displayImages, original_images: photos, watermark } }); imagesSaved = true; } catch (e) { toast.error(`Photos non enregistrées${e?.message ? ` : ${e.message}` : ""}. Réessayez — rien n'a été supprimé.`, { duration: 9000 }); }
    // Only drop files once the new list is saved, and never one still in use.
    const toDelete = removed.filter((u) => !photos.includes(u) && !displayImages.includes(u));
    if (imagesSaved && toDelete.length) { try { await crm("/media/delete", { method: "POST", body: { paths: toDelete } }); } catch {} }
    try { await crm("/car-meta/condition", { method: "POST", body: { car_id: car.id, accidented, damage_photos: accidented ? damageZones.flatMap((z) => z.photos || []).slice(0, 8) : [], damage_zones: accidented ? damageZones : [], honesty_note: accidented ? honestyNote : "", vat_deductible: !!f.vat_deductible, phone: f.phone || "" } }); } catch {}

    // Vehicle category override (car ↔ moto/van/…) — drives the public tab, badge and filters.
    try { await crm("/car-meta/category", { method: "POST", body: { car_id: car.id, vehicle_type: vt, bike_type: vt === "moto" ? (bikeType || null) : null, engine_cc: f.displacement !== "" ? Number(f.displacement) : null } }); } catch {}

    setSaving(false);
    if (!imagesSaved) return; // keep the modal open so the seller can retry; error already shown
    if (patchErr) toast.error(patchErr.message || "Certaines informations n'ont pas pu être enregistrées.");
    else toast.success(t("pro.updated"));
    await onSaved();
  };

  const selCls = "h-10 w-full rounded-md border border-white/10 bg-white/5 px-3 text-sm text-white outline-none focus:border-primary";
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-white/10 bg-pactly-ink2 text-white">
        <DialogHeader><DialogTitle className="font-display">{t("pro.editListing")} · {car.make} {car.model}</DialogTitle>
          <DialogDescription className="text-muted-foreground">{car.ref || `${car.year} · ${formatKm(car.mileage)}`}</DialogDescription>
        </DialogHeader>
        <PriceAdvice carId={car.id} />
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2" data-testid="edit-vehicle-type">
            <div className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-white/50">Type de véhicule</div>
            <div className="flex flex-wrap gap-1.5">
              {VEHICLE_TYPES.map((vtype) => {
                const on = vt === vtype.key;
                const Icon = vtype.icon;
                return (
                  <button key={vtype.key} type="button" data-testid={`edit-vtype-${vtype.key}`}
                    onClick={() => setVt(vtype.key)}
                    className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-semibold transition ${on ? "gradient-brand border-transparent text-white" : "border-white/15 bg-white/5 text-white/70 hover:border-white/30"}`}>
                    <Icon size={15} /> {vtype.label}
                  </button>
                );
              })}
            </div>
            {vt === "moto" && (
              <p className="mt-1.5 flex items-center gap-1.5 text-[11px] text-pactly-violet" data-testid="edit-vtype-hint"><Bike size={12} /> Formulaire adapté aux motos (portes, places et carrosserie masqués).</p>
            )}
          </div>
          <L label={t("f.make")}>
            <MakeCombobox testid="edit-make" value={f.make}
              options={(makes || []).map((m) => ({ name: m.name, logo: m.logo }))}
              onChange={(name) => setF((p) => ({ ...p, make: name || "", model: "", variant: "" }))} />
          </L>
          <L label={t("f.model")}>
            <MakeCombobox testid="edit-model" value={f.model} disabled={!f.make}
              placeholder={f.make ? "—" : t("f.pickMakeFirst")} emptyText={t("f.pickMakeFirst")}
              options={models.map((m) => ({ name: m.name }))}
              onChange={(name) => setF((p) => ({ ...p, model: name || "", variant: "" }))} />
          </L>
          {vt === "car" && (
            <L label={t("sell.variantLabel")} className="sm:col-span-2">
              <MakeCombobox testid="edit-variant" value={f.variant} placeholder="Motorisation (320d, 330e…)"
                options={variantOpts.map((v) => ({ name: v }))}
                onChange={(name) => upd("variant", name || "")} />
            </L>
          )}
          <L label="Prix (€)"><Input data-testid="edit-price" type="number" value={f.price} onChange={(e) => upd("price", e.target.value)} className="bg-white/5 text-white" /></L>
          <L label={t("d.mileage") + " (km)"}><Input data-testid="edit-mileage" type="number" value={f.mileage} onChange={(e) => upd("mileage", e.target.value)} className="bg-white/5 text-white" /></L>
          <L label={t("edit.year")}><Input data-testid="edit-year" type="number" value={f.year} onChange={(e) => upd("year", e.target.value)} className="bg-white/5 text-white" /></L>
          <L label={t("d.firstReg")}><Input data-testid="edit-first-reg" value={f.first_registration} onChange={(e) => upd("first_registration", e.target.value)} placeholder="MM/AAAA" className="bg-white/5 text-white" /></L>
          <L label={t("d.power") + " (ch)"}><Input data-testid="edit-power" type="number" value={f.power} onChange={(e) => upd("power", e.target.value)} className="bg-white/5 text-white" /></L>
          <L label={t("f.fuel")}><select data-testid="edit-fuel" value={f.fuel} onChange={(e) => upd("fuel", e.target.value)} className={selCls}><option value="" className="bg-pactly-ink2">—</option>{FUEL_OPTIONS.map((o) => opt(o.v, o[lang] || o.en, { key: o.v, className: "bg-pactly-ink2" }))}</select></L>
          <L label={t("f.transmission")}><select data-testid="edit-transmission" value={f.transmission} onChange={(e) => upd("transmission", e.target.value)} className={selCls}><option value="" className="bg-pactly-ink2">—</option>{["Automatic", "Manual"].map((o) => opt(o, o, { key: o, className: "bg-pactly-ink2" }))}</select></L>
          {vt === "moto" ? (
            <L label="Type de moto"><select data-testid="edit-bike-type" value={bikeType} onChange={(e) => setBikeType(e.target.value)} className={selCls}><option value="" className="bg-pactly-ink2">—</option>{BIKE_TYPES.map((o) => opt(o, o, { key: o, className: "bg-pactly-ink2" }))}</select></L>
          ) : (
            <L label={t("f.body")}><select data-testid="edit-body" value={f.body_type} onChange={(e) => upd("body_type", e.target.value)} className={selCls}><option value="" className="bg-pactly-ink2">—</option>{["Berline", "SUV / 4x4", "Coupé", "Break", "Citadine / Compacte", "Monospace", "Cabriolet", "Pick-up"].map((o) => opt(o, o, { key: o, className: "bg-pactly-ink2" }))}</select></L>
          )}
          <L label={t("d.color")}><ColorSelect value={f.color} onChange={(v) => upd("color", v)} testid="edit-color" /></L>
          {vt === "moto" ? (
            <L label="Cylindrée (cm³)"><Input data-testid="edit-moto-cc" type="number" value={f.displacement} onChange={(e) => upd("displacement", e.target.value)} placeholder="ex. 1250" className="bg-white/5 text-white" /></L>
          ) : (
            <>
              <L label={t("d.doors")}><Input data-testid="edit-doors" type="number" value={f.doors} onChange={(e) => upd("doors", e.target.value)} className="bg-white/5 text-white" /></L>
              <L label={t("d.seats")}><Input data-testid="edit-seats" type="number" value={f.seats} onChange={(e) => upd("seats", e.target.value)} className="bg-white/5 text-white" /></L>
            </>
          )}
          <L label={t("f.condition")}><select data-testid="edit-condition" value={f.condition} onChange={(e) => upd("condition", e.target.value)} className={selCls}>{["Occasion", "Neuf en stock", "Modèle de démonstration", "Neuf sur commande"].map((o) => opt(o, o, { key: o, className: "bg-pactly-ink2" }))}</select></L>
          <div className="sm:col-span-2 flex items-center justify-between gap-3 rounded-xl border border-pactly-violet/30 bg-pactly-violet/10 px-3 py-2">
            <div className="min-w-0 text-xs text-white/70">
              <span className="font-semibold text-white">Import fiscalité</span> — remplissez CO₂ / cylindrée / norme Euro automatiquement.
            </div>
            <input ref={cgRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="edit-cg-input"
              onChange={(e) => { const file = e.target.files?.[0]; if (e.target) e.target.value = ""; importCarteGrise(file); }} />
            <button type="button" data-testid="edit-scan-carte-grise" disabled={cgBusy} onClick={() => cgRef.current?.click()}
              className="on-dark flex shrink-0 items-center gap-1.5 rounded-lg bg-pactly-violet px-3 py-2 text-sm font-bold text-white transition hover:opacity-90 disabled:opacity-60">
              {cgBusy ? <Loader2 size={15} className="animate-spin" /> : <ScanLine size={15} />} {cgBusy ? "Analyse…" : "Scanner (carte grise / COC)"}
            </button>
          </div>
          <L label="CO₂ (g/km)"><Input data-testid="edit-co2" type="number" value={f.co2} onChange={(e) => upd("co2", e.target.value)} className="bg-white/5 text-white" /></L>
          <L label="Cylindrée (cm³)"><Input data-testid="edit-displacement" type="number" value={f.displacement} onChange={(e) => upd("displacement", e.target.value)} className="bg-white/5 text-white" /></L>
          <L label="Norme Euro"><select data-testid="edit-euro" value={f.euro_norm} onChange={(e) => upd("euro_norm", e.target.value)} className={selCls}><option value="" className="bg-pactly-ink2">—</option>{EURO_NORMS.map((o) => opt(o, o, { key: o, className: "bg-pactly-ink2" }))}</select></L>
          <L label="Téléphone (bouton Appeler)"><Input data-testid="edit-phone" type="tel" value={f.phone} onChange={(e) => upd("phone", e.target.value)} placeholder="+32 4XX XX XX XX" className="bg-white/5 text-white" /></L>
          <L label={t("f.location")} className="sm:col-span-2"><Input data-testid="edit-location" value={f.location} onChange={(e) => upd("location", e.target.value)} className="bg-white/5 text-white" /></L>
          {isEV(f.fuel) && (
            <div className="theme-electric force-dark sm:col-span-2 rounded-xl border border-[#00F0FF]/25 bg-[#00F0FF]/[0.05] p-4" data-testid="edit-ev-block">
              <div className="mb-3 flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#00F0FF]/15 text-[#00F0FF]"><Zap size={15} /></span>
                <div>
                  <h3 className="font-display text-sm font-bold text-white">Fiche électrique {isPureElectric(f.fuel) ? "· 100% Électrique" : "· Hybride"}</h3>
                  <p className="text-[11px] text-[#7fdfff]">Batterie, santé, recharge & autonomie — affichées sur la fiche.</p>
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <L label="Capacité batterie (kWh)"><Input data-testid="edit-ev-kwh" type="number" step="0.1" value={ev.battery_kwh} onChange={(e) => updEv("battery_kwh", e.target.value)} placeholder="ex. 58" className="bg-white/5 text-white" /></L>
                <L label="Santé batterie SOH (%)"><Input data-testid="edit-ev-soh" type="number" min="0" max="100" value={ev.soh} onChange={(e) => updEv("soh", e.target.value)} placeholder="ex. 94" className="bg-white/5 text-white" /></L>
                <L label="Puissance de charge max (kW)"><Input data-testid="edit-ev-charge" type="number" value={ev.charge_kw} onChange={(e) => updEv("charge_kw", e.target.value)} placeholder="ex. 150" className="bg-white/5 text-white" /></L>
                <L label="Autonomie WLTP (km)"><Input data-testid="edit-ev-range" type="number" value={ev.range_wltp} onChange={(e) => updEv("range_wltp", e.target.value)} placeholder="ex. 420" className="bg-white/5 text-white" /></L>
              </div>
            </div>
          )}
        </div>

        <label className="mt-1 flex cursor-pointer items-center justify-between rounded-xl border border-white/10 bg-white/5 px-4 py-2.5" data-testid="edit-negotiable-row">
          <span className="text-sm font-medium text-white/85">{t("edit.negotiable")}</span>
          <input type="checkbox" checked={!!f.negotiable} onChange={(e) => upd("negotiable", e.target.checked)} className="h-5 w-5 accent-[#1E5288]" data-testid="edit-negotiable" />
        </label>

        <label className="flex cursor-pointer items-center justify-between rounded-xl border border-white/10 bg-white/5 px-4 py-2.5" data-testid="edit-vat-row">
          <span className="text-sm font-medium text-white/85">{t("d.vatDeductible")}</span>
          <input type="checkbox" checked={!!f.vat_deductible} onChange={(e) => upd("vat_deductible", e.target.checked)} className="h-5 w-5 accent-[#1E5288]" data-testid="edit-vat-deductible" />
        </label>

        <label className={`flex cursor-pointer items-center justify-between rounded-xl border px-4 py-2.5 transition ${accidented ? "border-amber-400/50 bg-amber-400/10" : "border-white/10 bg-white/5"}`} data-testid="edit-accidented-row">
          <span className="flex items-center gap-2 text-sm font-semibold text-white/90">
            <AlertTriangle size={16} className={accidented ? "text-amber-400" : "text-white/50"} /> {t("edit.accidented")}
          </span>
          <input type="checkbox" checked={accidented} onChange={(e) => setAccidented(e.target.checked)} className="h-5 w-5 accent-[#f59e0b]" data-testid="edit-accidented" />
        </label>

        {accidented && <DamageInput note={honestyNote} zones={damageZones} onNote={setHonestyNote} onZones={setDamageZones} />}

        {/* Photo management: view / remove / add / drag-reorder listing photos */}
        <div className="rounded-xl border border-white/10 bg-white/5 p-3" data-testid="edit-photos">
          <div className="mb-1 flex items-center justify-between">
            <span className="flex items-center gap-2 text-sm font-semibold text-white/90"><ImagePlus size={15} className="text-pactly-violet" /> {t("edit.photos")}</span>
            <div className="flex items-center gap-2">
              <button type="button" data-testid="edit-import-phone-btn" onClick={startPhoneImport} disabled={photos.length >= MAX_LISTING_PHOTOS}
                className="flex items-center gap-1.5 rounded-lg border border-pactly-violet/40 bg-pactly-violet/10 px-2.5 py-1.5 text-xs font-bold text-white transition hover:bg-pactly-violet/20 disabled:opacity-50">
                <Smartphone size={13} /> Téléphone
              </button>
              <span className="text-xs text-muted-foreground">{photos.length}/{MAX_LISTING_PHOTOS}</span>
            </div>
          </div>
          {photos.length > 1 && <p className="mb-2 text-[11px] text-muted-foreground">{t("edit.reorderHint")}</p>}
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
            {photos.map((src, i) => (
              <div key={`${src}-${i}`} data-testid={`edit-photo-${i}`}
                draggable
                onDragStart={() => setDragIdx(i)}
                onDragEnter={() => setOverIdx(i)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); reorder(dragIdx, i); setDragIdx(null); setOverIdx(null); }}
                onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
                className={`group relative aspect-[4/3] cursor-move overflow-hidden rounded-lg border transition ${overIdx === i && dragIdx !== null ? "border-pactly-violet ring-2 ring-pactly-violet/60" : "border-white/10"} ${dragIdx === i ? "opacity-50" : ""}`}>
                <PhotoThumb src={mediaUrl(src)} className="pointer-events-none h-full w-full object-cover" testid={`edit-photo-img-${i}`} />
                {i === 0
                  ? <span className="absolute left-1 top-1 rounded gradient-brand px-1.5 py-0.5 text-[9px] font-extrabold text-white">{t("sell.mainPhoto")}</span>
                  : <span className="absolute left-1 top-1 hidden h-5 w-5 items-center justify-center rounded bg-black/60 text-white/80 sm:flex"><GripVertical size={12} /></span>}
                <button type="button" data-testid={`edit-remove-photo-${i}`} onClick={() => removePhoto(i)}
                  className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/70 text-white transition hover:bg-red-500"><X size={13} /></button>
                {/* Mobile-friendly reorder arrows — the 1st photo is the main one */}
                <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-black/70 to-transparent p-1">
                  <button type="button" data-testid={`edit-move-left-${i}`} disabled={i === 0} onClick={() => reorder(i, i - 1)}
                    className="flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white transition hover:bg-pactly-violet disabled:opacity-25" aria-label={t("edit.moveLeft")}><ChevronLeft size={14} /></button>
                  <button type="button" data-testid={`edit-move-right-${i}`} disabled={i === photos.length - 1} onClick={() => reorder(i, i + 1)}
                    className="flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white transition hover:bg-pactly-violet disabled:opacity-25" aria-label={t("edit.moveRight")}><ChevronRight size={14} /></button>
                </div>
              </div>
            ))}
            {photos.length < MAX_LISTING_PHOTOS && (
              <button type="button" data-testid="edit-add-photo-btn" onClick={() => photoInputRef.current?.click()} disabled={photoBusy > 0}
                className="flex aspect-[4/3] flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-white/15 text-muted-foreground transition hover:border-primary hover:text-white disabled:opacity-50">
                {photoBusy > 0 ? <Loader2 size={18} className="animate-spin" /> : <ImagePlus size={18} />}
                <span className="text-[10px] font-semibold">{photoBusy > 0 ? t("common.loading") : t("sell.addPhotos")}</span>
              </button>
            )}
          </div>
          <input ref={photoInputRef} type="file" accept="image/*" multiple className="hidden" onChange={onAddPhotos} data-testid="edit-photo-input" />
        </div>

        {/* Filigrane (protection photos) — non destructif : on garde les originaux */}
        <div className="rounded-xl border border-white/10 bg-white/5 p-3" data-testid="edit-watermark-block">
          <label className={`flex cursor-pointer items-center justify-between rounded-lg border px-3 py-2 transition ${watermark ? "border-pactly-violet/50 bg-pactly-violet/10" : "border-white/10"}`} data-testid="edit-watermark-row">
            <span className="flex items-center gap-2 text-sm font-semibold text-white/90">
              <ImagePlus size={16} className={watermark ? "text-pactly-violet" : "text-white/50"} /> Filigrane sur les photos
            </span>
            <input type="checkbox" checked={watermark} onChange={(e) => setWatermark(e.target.checked)} className="h-5 w-5 accent-pactly-violet" data-testid="edit-watermark-checkbox" />
          </label>
          <p className="mt-1.5 text-[11px] text-muted-foreground" data-testid="edit-watermark-hint">
            {watermark
              ? "Vos photos seront protégées par le filigrane à l'enregistrement."
              : "Le filigrane sera retiré des photos à l'enregistrement."}
            {!hasCleanOriginals && (
              <span className="mt-1 block text-amber-300/90">
                ⚠️ Cette annonce a été créée avant la mise à jour : le filigrane existant est incrusté et ne peut pas être retiré. Il pourra seulement être (ré)appliqué.
              </span>
            )}
          </p>
          {watermark && photos[0] && (
            <div className="mt-3" data-testid="edit-watermark-preview">
              <div className="mb-1 text-[11px] font-semibold text-white/60">Aperçu du filigrane (photo principale)</div>
              <WatermarkPreview src={photos[0]} text={wmText} className="w-full max-w-md rounded-lg border border-white/10" />
            </div>
          )}
        </div>

        <div className="rounded-xl border border-white/10 bg-white/5 p-3" data-testid="edit-options">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-semibold text-white/90">{t("d.options")}</span>
            <span className="text-xs text-muted-foreground">{options.length}</span>
          </div>
          <div className="max-h-56 space-y-3 overflow-y-auto pr-1">
            {OPTION_GROUPS.map((g) => (
              <div key={g.label}>
                <div className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-white/45">{g.label}</div>
                <div className="flex flex-wrap gap-1.5">
                  {g.items.map((o) => {
                    const on = options.includes(o);
                    return (
                      <button key={o} type="button" data-testid={`edit-option-${o}`} onClick={() => toggleOption(o)}
                        className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold transition ${on ? "gradient-brand border-transparent text-white" : "border-white/15 bg-white/5 text-white/70 hover:border-white/30"}`}>
                        {o}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        <L label={t("d.description")}><Textarea data-testid="edit-description" rows={3} value={f.description} onChange={(e) => upd("description", e.target.value)} className="bg-white/5 text-white" /></L>
        <Button data-testid="edit-save" onClick={save} disabled={saving} className="w-full gradient-brand font-bold text-white">{saving ? t("common.saving") : t("common.save")}</Button>
      </DialogContent>

      {photoQrOpen && (
        <div className="fixed inset-0 z-[140] flex items-center justify-center bg-[#050a18]/85 p-4 backdrop-blur-sm" data-testid="edit-photo-qr-modal" onClick={closePhoneImport}>
          <div onClick={(e) => e.stopPropagation()} className="relative w-full max-w-sm overflow-hidden rounded-3xl border border-white/12 bg-[#0d1428]/95 p-6 text-white shadow-2xl">
            <button type="button" onClick={closePhoneImport} data-testid="edit-photo-qr-close"
              className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full text-white/50 transition hover:bg-white/10 hover:text-white"><X size={17} /></button>
            <div className="flex items-center gap-2.5">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 ring-1 ring-white/15"><Smartphone size={20} className="text-slate-200" /></div>
              <div><div className="font-display text-lg font-bold">Photos depuis le téléphone</div><div className="text-xs text-slate-400">Transfert en direct vers l'annonce</div></div>
            </div>
            <div className="mt-5 flex flex-col items-center">
              {photoQr ? <div className="rounded-2xl bg-white p-3" data-testid="edit-photo-qr-image"><img src={photoQr} alt="QR" className="h-40 w-40" /></div>
                : <div className="flex h-40 w-40 items-center justify-center rounded-2xl bg-white/5"><Loader2 size={26} className="animate-spin text-white/40" /></div>}
              <div className="mt-4 flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1.5 text-xs font-bold text-emerald-300" data-testid="edit-photo-qr-received">
                {photoRecv > 0 ? <><CheckCircle2 size={14} /> {photoRecv} photo{photoRecv > 1 ? "s" : ""} reçue{photoRecv > 1 ? "s" : ""}</> : <><Loader2 size={13} className="animate-spin" /> En attente…</>}
              </div>
            </div>
            <p className="mt-5 text-center text-xs text-slate-300">Scannez ce QR code avec votre téléphone, choisissez vos photos — elles arrivent ici automatiquement.</p>
            <Button type="button" onClick={closePhoneImport} data-testid="edit-photo-qr-done" className="mt-4 w-full gradient-brand font-bold text-white">{photoRecv > 0 ? "Terminé" : "Fermer"}</Button>
          </div>
        </div>
      )}
    </Dialog>
  );
}
